/**
 * ServiceCard Component
 *
 * Renders a styled card for displaying a service with an icon, title, and description.
 * Features:
 * - Responsive width for mobile and desktop
 * - Animated shadow and scale on hover for interactivity
 * - Accepts an icon, title, and description as props
 *
 * @param {ServiceCardProps} props - Props for the ServiceCard component
 * @param {React.ReactNode} props.icon - The icon to display in the card (SVG or React element)
 * @param {string} props.title - The service title to display
 * @param {React.ReactNode} props.description - The service description (can be string or JSX)
 *
 * @returns {JSX.Element} The rendered service card
 */
'use client';

import React from 'react';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}

/**
 * Renders a single service card with icon, title, and description.
 */
export default function ServiceCard({ icon, title, description }: ServiceCardProps) {
  return (
    <div className="bg-white border border-black/10 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group hover:scale-[1.02] w-full md:w-[433px]">
      <div className="flex flex-col gap-4">
        {/* Icon container */}
        <div className="w-[50px] h-[50px] flex items-center justify-center bg-androia-blue-mid rounded-full">
          {icon}
        </div>
        {/* Service title */}
        <h3 className="font-manrope font-semibold text-androia-text text-2xl tracking-[-0.48px] leading-[40px]">
          {title}
        </h3>
        {/* Service description */}
        <p className="font-manrope font-medium text-lg tracking-[-0.36px] leading-8">
          {description}
        </p>
      </div>
    </div>
  );
}
